import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import { openAppConfirm } from "../../components/AppDialogs";
import { CardBody, CardHeader, CardTitle, Form } from "reactstrap";
import MDButton from "components/MDButton";
import { GetSlideByIdCall } from "../../redux/api/title.api";
import { DeleteFile } from "../../redux/api/upload.api";
import { store } from "../../redux/store";
import { basicURL } from "../../apiManager/APIConfig";
import { useMaterialUIController } from "../../context";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import RelatedModelsPanel from "./components/RelatedModelsPanel";
import "./map.css";

const PUBLIC_SLIDE_TYPES = ["his", "emb", "three_d_model", "emb_3d_model", "presentation"];
const CORE_TYPES = ["his", "emb", "three_d_model", "emb_3d_model"];
const defaultModelUrl = "https://modelviewer.dev/shared-assets/models/shishkebab.glb";

function ShowThreeD() {
  const fileInputRef = useRef();
  const { id, lang } = useParams();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [slideData, setSlideData] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [modelUrl, setModelUrl] = useState(defaultModelUrl);
  const [activeDeleteButton, setActiveDeleteButton] = useState(false);

  const role = store.getState().auth?.role;
  const isPublicSlide = PUBLIC_SLIDE_TYPES.includes(
    slideData?.parentTitle?.type || slideData?.Titles?.type || slideData?.titles?.type || ""
  );
  const canEditSlide = !isPublicSlide || role === "admin";
  const directTitleType =
    slideData?.parentTitle?.type || slideData?.Titles?.type || slideData?.titles?.type || "";
  const rootTitleType = slideData?.parentTitle?.parentTitle?.type || "";
  const resolvedCoreType = CORE_TYPES.includes(directTitleType)
    ? directTitleType
    : CORE_TYPES.includes(rootTitleType)
      ? rootTitleType
      : "";
  const sourceTitleId =
    slideData?.parentTitle?.id || slideData?.Titles?.id || slideData?.titles?.id || null;

  // Fetch slide and get model URL
  useEffect(() => {
    let mounted = true;
    const fetchSlide = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const includePrivatePins = params.get("private") === "1";
        const res = await GetSlideByIdCall({ id, lang, includePrivatePins });
        if (!mounted) return;
        // Handle error response (e.g. 404/500) – body may have status or just message, no slide id
        const isError =
          (res && typeof res.status === "number" && res.status >= 400) ||
          (res?.message && res?.id == null);
        if (isError) {
          setSlideData(null);
          setModelUrl(defaultModelUrl);
          setActiveDeleteButton(false);
          return;
        }
        const slide = res?.data ?? res;
        setSlideData(slide);
        const files = slide?.Files ?? slide?.files ?? [];
        const first = Array.isArray(files) ? files[0] : null;
        let url = first?.url ?? first?.path ?? null;
        if (url != null && typeof url === "string") {
          const trimmed = url.trim();
          if (trimmed) {
            if (!trimmed.startsWith("http://") && !trimmed.startsWith("https://")) {
              const base = (basicURL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
              url = trimmed.startsWith("/") ? base + trimmed : `${base}/${trimmed}`;
            } else {
              url = trimmed;
            }
          } else {
            url = null;
          }
        } else {
          url = null;
        }
        setModelUrl(url || defaultModelUrl);
        setActiveDeleteButton(Boolean(url));
      } catch (e) {
        if (mounted) {
          setSlideData(null);
          setModelUrl(defaultModelUrl);
          setActiveDeleteButton(false);
        }
      }
    };
    fetchSlide();
    return () => { mounted = false; };
  }, [id, lang]);

  // Use iframe so model-viewer runs in its own document (avoids React/custom-element issues)
  const themeParam = darkMode ? "dark" : "light";
  const iframeSrc =
    typeof window !== "undefined"
      ? `${window.location.origin}/model-viewer.html?src=${encodeURIComponent(modelUrl || defaultModelUrl)}&theme=${themeParam}`
      : "";

  // Handle upload or delete
  const handleAddMaterial = async (event) => {
    if (activeDeleteButton) {
      const file_id = slideData?.Files?.[0]?.id ?? slideData?.files?.[0]?.id;
      const confirmed = await openAppConfirm({
        title: "Delete slide?",
        message: "Are you sure you want to delete this slide? Your pins will remain.",
        confirmText: "Delete",
        confirmColor: "error",
      });
      if (confirmed) {
        await DeleteFile(file_id);
        window.location.reload();
      }
    } else {
      const file = event.target.files[0];
      if (!file) return;

      try {
        setShowLoader(true);
        const formData = new FormData();
        formData.append("file", file);
        formData.append("entity_type", "slide");
        formData.append("entity_id", id);
        formData.append("destination", "slides");
        formData.append("folder_name", "slides");

        const access_token = store.getState().auth?.access_token;
        const headers = { Authorization: access_token };

        const response = await axios.post(`${basicURL}/file/upload`, formData, {
          headers,
          onUploadProgress: (event) => {
            const percent = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(percent);
          },
        });

        setShowLoader(false);
        const glbUrl = `${basicURL}/${response?.data?.extracted_files?.[1]?.path}`;
        setModelUrl(glbUrl);
        window.location.reload();
      } catch (err) {
        console.error("Upload error:", err);
        setShowLoader(false);
      }
    }
  };

  const uploaderTitle =
    uploadProgress !== 100
      ? `Upload Progress: ${uploadProgress}%`
      : "Extracting the zip file, this may take a while...";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className="content">
        <CardHeader>
          <CardTitle tag="h5">
            {canEditSlide && !activeDeleteButton ? (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleAddMaterial}
                />
                <MDButton
                  color="info"
                  variant="contained"
                  onClick={() => fileInputRef.current.click()}
                  style={{ marginBottom: 15 }}
                >
                  Upload Slide
                </MDButton>
              </>
            ) : canEditSlide && activeDeleteButton ? (
              <MDButton
                color="primary"
                variant="contained"
                onClick={() => handleAddMaterial()}
                style={{ marginBottom: 15 }}
              >
                Delete Model
              </MDButton>
            ) : isPublicSlide ? (
              <span style={{ marginBottom: 15, color: "#666", fontSize: 14 }}>
                View only — only admin can upload or delete public 3D models.
              </span>
            ) : null}
          </CardTitle>
          {showLoader && (
            <CardBody>
              <Form>
                <div>
                  <div>{uploaderTitle}</div>
                  <div style={{ marginBottom: 20, height: 10, backgroundColor: "#ddd" }}>
                    <div
                      style={{
                        height: "100%",
                        width: `${uploadProgress}%`,
                        backgroundColor: "green",
                      }}
                    ></div>
                  </div>
                </div>
              </Form>
            </CardBody>
          )}
        </CardHeader>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "flex-start",
            gap: 16,
            width: "100%",
          }}
        >
          <div className="container" style={{ padding: 0, maxWidth: "100%", flex: 1 }}>
            {!modelUrl ? (
              <div className="loadingOrErrorView">
                <div>No 3D model found</div>
              </div>
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "80vh",
                  minHeight: 480,
                  borderRadius: 8,
                  overflow: "hidden",
                  background: darkMode ? "#1a1a1a" : "#f5f5f5",
                }}
              >
                <iframe
                  key={modelUrl}
                  src={iframeSrc}
                  title="3D Model viewer"
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 480,
                    border: "none",
                    display: "block",
                  }}
                />
              </div>
            )}
          </div>
          <div
            style={{
              width: isMobile ? "100%" : 320,
              flexShrink: 0,
              position: isMobile ? "static" : "sticky",
              top: isMobile ? "auto" : 88,
            }}
          >
            <RelatedModelsPanel
              sourceTitleId={sourceTitleId}
              sourceType={resolvedCoreType}
              isAdmin={String(role || "").toLowerCase() === "admin"}
              lang={lang}
              refreshKey={slideData?.id || 0}
              compact={!isMobile}
              minWidth={isMobile ? 0 : 300}
              maxWidth={isMobile ? 1200 : 320}
            />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

export default ShowThreeD;
